import { useCallback, useMemo, useState } from 'react';
import { produce } from 'immer';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableHead,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableSkeletonRows,
} from '@antimatterhq/ui/components/simpleTable';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import { ObservableKindDialog } from '@/components/dialog';
import { bumpMetadataVersion } from '@/utils/util';
import { ObservableKind as ObservableKindType } from '@/zod/schemas/workspaceConfig/ObservableKind';

export type ObservableKind = {
  name: string;
  sqlType: ObservableKindType['sqlType'];
};

export const ObservableKinds = () => {
  const { data, isPending } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig } = useUpdateWorkspaceConfig();
  const [activeKind, setActiveKind] = useState<ObservableKind | undefined>(
    undefined,
  );
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();
  const [editIndex, setEditIndex] = useState<number | undefined>(undefined);

  const kinds = useMemo(() => {
    return (data?.spec.observables?.kinds || []).filter(
      (kind): kind is ObservableKindType => !!kind.name && !!kind.sqlType,
    );
  }, [data]);

  const onSubmit = useCallback(
    async (updatedKinds: ObservableKind[]) => {
      if (!data) {
        return;
      }

      const config = produce(data, (draft) => {
        if (!draft.spec.observables) {
          draft.spec.observables = { kinds: [] };
        }
        draft.spec.observables.kinds = [...updatedKinds];
        // TODO: remove later
        if (!draft.spec.systemTablesConfig) {
          draft.spec.systemTablesConfig = {
            schema: 'antimatter_meta',
            catalogName: 'databricks_dev',
          };
        }
        bumpMetadataVersion(draft);
      });

      optimisticUpdate(config);
      await updateConfig(config, {
        toasts: {
          success: 'Observable kinds updated',
          error: 'Failed to update observable kinds',
        },
      });
    },
    [data, updateConfig, optimisticUpdate],
  );

  const addKind = useCallback(
    (kind: ObservableKind) => {
      const updatedKinds = [...kinds, { ...kind }];
      onSubmit(updatedKinds);
    },
    [onSubmit, kinds],
  );

  const updateKind = useCallback(
    (kind: ObservableKind) => {
      const updatedKinds = kinds.map((o, index) =>
        index === editIndex ? { ...kind } : o,
      );
      onSubmit(updatedKinds);

      setActiveKind(undefined);
      setEditIndex(undefined);
    },
    [editIndex, onSubmit, kinds],
  );

  const removeKind = useCallback(
    (observable: ObservableKind) => {
      const updatedKinds = kinds.filter((o) => o.name !== observable.name);
      onSubmit(updatedKinds);
    },
    [kinds, onSubmit],
  );

  const onSubmitKind = useCallback(
    async (kind: ObservableKind) => {
      if (editIndex !== undefined) {
        updateKind(kind);
      } else {
        addKind(kind);
      }
      setActiveKind(undefined);
    },
    [addKind, updateKind, editIndex],
  );

  const existingKinds = useMemo(() => {
    return kinds.map((k) => k.name);
  }, [kinds]);

  return (
    <>
      <SimpleTable className="w-full" fixed>
        <SimpleTableHeader>
          <SimpleTableRow hover={false}>
            <SimpleTableHead className="font-bold">Observable</SimpleTableHead>
            <SimpleTableHead className="font-bold">SQL type</SimpleTableHead>
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {!isPending &&
            kinds.map((observable, index) => (
              <SimpleTableRow key={observable.name} className="h-[31px]">
                <SimpleTableCell
                  className="relative w-[50%] truncate"
                  data-testid={`kind-name-${observable.name}`}
                >
                  {observable.name}
                </SimpleTableCell>
                <SimpleTableCell className="relative w-[50%] truncate">
                  {observable.sqlType}
                  <div className="invisible absolute right-0 top-0 flex gap-1">
                    <Button
                      iconOnly
                      leftIcon="antimatterhq:pencil"
                      size="small"
                      variant="ghost"
                      className="p-0"
                      onClick={() => {
                        setActiveKind(observable);
                        setEditIndex(index);
                      }}
                      data-testid={`edit-kind-${observable.name}-button`}
                    />
                    <Button
                      iconOnly
                      leftIcon="antimatterhq:trash"
                      size="small"
                      variant="ghost"
                      className="p-0"
                      onClick={() => removeKind(observable)}
                      data-testid={`remove-kind-${observable.name}-button`}
                    />
                  </div>
                </SimpleTableCell>
              </SimpleTableRow>
            ))}
          {isPending && (
            <SimpleTableSkeletonRows cols={2} height={31} rows={5} />
          )}
        </SimpleTableBody>
      </SimpleTable>

      <Spacer size="lg" />

      <div className="flex justify-end gap-2">
        <Button
          variant="default"
          leftIcon="radix-icons:plus"
          onClick={() => setActiveKind({ name: '', sqlType: '' })}
          data-testid="add-kind-button"
        >
          Add an observable kind
        </Button>
      </div>
      {!!activeKind && (
        <ObservableKindDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setActiveKind(undefined);
            }
          }}
          onSave={onSubmitKind}
          kind={activeKind || { name: '', sqlType: '' }}
          isEdit={editIndex !== undefined}
          existingKinds={existingKinds}
        />
      )}
    </>
  );
};
